import React from 'react';
import { GameType, ViewType } from '../types';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

interface HomeViewProps {
  onNavigate: (view: ViewType) => void;
  onOpenGame: (game: GameType, mode?: 'normal' | 'competition') => void;
  onOpenLeaderboard: (game?: 'tower' | 'competition' | 'draw' | 'puzzle') => void;
  onScrollToGame?: (game: 'tower' | 'draw' | 'puzzle') => void;
  isVideoPlaying: boolean;
  onToggleVideo: () => void;
  isMuted: boolean;
  onToggleSound: () => void;
  videoRef: React.RefObject<HTMLVideoElement | null>;
}

export const HomeView: React.FC<HomeViewProps> = ({
  onNavigate,
  onOpenGame,
  onOpenLeaderboard,
  onScrollToGame,
  isVideoPlaying,
  onToggleVideo,
  isMuted,
  onToggleSound,
  videoRef,
}) => {
  const scrollToPlay = () => {
    const el = document.getElementById('play-now');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToGame = (game: 'tower' | 'draw' | 'puzzle') => {
    if (onScrollToGame) {
      onScrollToGame(game);
    } else {
      const targetId = `game-${game}`;
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-[#ffd700]');
        setTimeout(() => el.classList.remove('ring-2', 'ring-[#ffd700]'), 2000);
      }
    }
  };

  return (
    <main className="world">
      {/* Skip link for accessibility */}
      <a className="skip-link" href="#play-now">
        Tới các trò chơi
      </a>

      {/* HERO FRAME */}
      <section className="hero-frame" aria-labelledby="world-title">
        {/* Background scene */}
        <div className="world-scene" aria-hidden="true">
          <img
            className="world-photo"
            src="/images/generated/heritage-carmine-background-v1.webp"
            alt="Mỹ thuật Việt Nam di sản"
            width="1600"
            height="860"
          />
          <video
            ref={videoRef}
            id="hero-video"
            className="world-video"
            autoPlay
            loop
            muted={isMuted}
            playsInline
            poster="/images/generated/heritage-carmine-background-v1.webp"
          >
            <source src="/video/vnfam-v2-hero-museum-journey.mp4" type="video/mp4" />
          </video>
          <div className="world-shade" />
        </div>

        {/* Video Controls: 2 nút tròn nhỏ ở góc phải trên của video */}
        <div className="absolute top-[74px] sm:top-[98px] right-3.5 sm:right-8 z-20 flex items-center gap-2">
          <button
            type="button"
            id="hero-video-toggle"
            onClick={onToggleVideo}
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/65 hover:bg-black/85 text-[#fff4dd] border border-[#e6ba55]/60 hover:border-[#ffd700] flex items-center justify-center shadow-lg backdrop-blur-md active:scale-90 transition-all cursor-pointer"
            aria-pressed={isVideoPlaying}
            aria-label={isVideoPlaying ? 'Tạm dừng video nền' : 'Tiếp tục phát video nền'}
            title={isVideoPlaying ? 'Tạm dừng video' : 'Tiếp tục phát video'}
          >
            {isVideoPlaying ? (
              <Pause className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#fbbf24] fill-current" />
            ) : (
              <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#34d399] fill-current ml-0.5" />
            )}
          </button>

          <button
            type="button"
            id="hero-sound-toggle"
            onClick={onToggleSound}
            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-lg backdrop-blur-md active:scale-90 transition-all cursor-pointer border ${
              !isMuted
                ? 'bg-[#0f2e26]/85 hover:bg-[#154237] text-[#6ee7b7] border-[#34d399]/80 shadow-[0_0_12px_rgba(52,211,153,0.35)]'
                : 'bg-black/65 hover:bg-black/85 text-[#f5d0c5]/80 hover:text-[#fff4dd] border-[#e6ba55]/60 hover:border-[#ffd700]'
            }`}
            aria-pressed={!isMuted}
            aria-label={isMuted ? 'Bật âm thanh video' : 'Tắt âm thanh video'}
            title={isMuted ? 'Bật âm thanh' : 'Tắt âm thanh'}
          >
            {!isMuted ? (
              <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#34d399] animate-pulse" />
            ) : (
              <VolumeX className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#f5d0c5]/80" />
            )}
          </button>
        </div>

        {/* Hero Intro Content */}
        <div className="world-main">
          <div className="world-intro">
            <p className="chapter">
              <span /> ✨ ĐẤU TRƯỜNG DI SẢN MỸ THUẬT · VNFAM
            </p>
            <h1 id="world-title">
              Bước vào
              <br />
              miền <em>kiệt tác.</em>
            </h1>
            <p className="world-description">
              Khám phá bảo vật, giải mã thử thách leo tháp và ghi danh Bảng Vàng!
            </p>
          </div>
        </div>

        {/* Bottom Hero Experience Dock */}
        <section
          className="experience-dock hero-experience"
          aria-label="Hành trình tại bảo tàng và sự kiện tuần"
        >
          <button
            type="button"
            className="museum-cta"
            onClick={() => onNavigate('journey')}
          >
            <span className="cta-symbol" aria-hidden="true">
              ✦
            </span>
            <span>
              <small>HÀNH TRÌNH THỰC TẾ</small>
              <strong>Thử thách tại bảo tàng</strong>
              <span>Quét QR · Mở câu chuyện · Sưu tập tem</span>
            </span>
            <b aria-hidden="true">↗</b>
          </button>

          <article className="weekly-banner">
            <img
              src="/images/artifacts/giong-nguyen-tu-nghiem.webp"
              alt="Tác phẩm Gióng"
            />
            <div className="weekly-copy">
              <p>
                <span className="live-dot" /> THI TUẦN ĐANG DIỄN RA{' '}
                <span className="sample-label">MẪU</span>
              </p>
              <h2>Vươn tới đỉnh tháp.</h2>
              <span>07–13.09.2026 · Săn cúp Bảng Vàng</span>
              <div className="weekly-actions">
                <button
                  type="button"
                  onClick={() => onOpenGame('tower', 'competition')}
                >
                  Tham gia thi <span aria-hidden="true">↗</span>
                </button>
                <button
                  id="weekly-leaderboard"
                  type="button"
                  onClick={() => onOpenLeaderboard('competition')}
                >
                  BXH Top 100
                </button>
              </div>
            </div>
          </article>
        </section>

        {/* Explore games floating CTA button */}
        <button
          type="button"
          className="explore-games-cta"
          onClick={scrollToPlay}
          aria-label="Khám phá ba trò chơi ở phần bên dưới"
        >
          <span>
            KHÁM PHÁ
            <br />
            TRÒ CHƠI
          </span>
          <b aria-hidden="true">↓</b>
        </button>
      </section>

      {/* 3 GAMES CHALLENGE FRAME */}
      <section className="challenge-frame scroll-mt-24 md:scroll-mt-28" id="play-now" aria-labelledby="play-title">
        <div className="challenge-frame__head">
          <p>ĐẤU TRƯỜNG TRÒ CHƠI DI SẢN</p>
          <h2 id="play-title">Chọn thử thách của bạn</h2>
          <span>03 GAME ĐANG MỞ · THI ĐẤU MIỄN PHÍ</span>
        </div>

        <aside className="play-rail" aria-label="Ba thử thách đang mở">
          {/* Game 1: Leo tháp */}
          <article className="play-item">
            <button
              id="game-tower"
              className="play-banner tower-banner scroll-mt-24"
              type="button"
              onClick={() => onOpenGame('tower', 'normal')}
              aria-label="Chơi Leo tháp mỹ thuật — chế độ thường"
              aria-describedby="game-tower-description"
            >
              <img
                src="/images/generated/game-tower-puzzle-realistic-v1.webp"
                alt="Người chơi giải đố trong phòng trưng bày"
              />
              <span className="banner-overlay" />
              <span className="banner-copy">
                <small>🏰 01 / LEO THÁP TRÍ TUỆ</small>
                <strong>
                  Leo tháp
                  <br />
                  mỹ thuật
                </strong>
                <span>
                  CHINH PHỤC 10 TẦNG <i aria-hidden="true">↗</i>
                </span>
              </span>
              <span className="play-arrow" aria-hidden="true">
                ↗
              </span>
            </button>
            <div className="play-description" id="game-tower-description">
              <span className="play-description__body">
                <span className="play-description__eyebrow">BẢN LĨNH NGƯỜI LEO THÁP</span>
                <strong>Mỗi tầng là một câu hỏi về mỹ thuật Việt Nam.</strong>
                <span className="play-description__copy">Trả lời đúng để đi tiếp, giữ 3 trái tim và chạm tới tầng cao nhất. Càng lên cao, thử thách càng bất ngờ.</span>
                <span className="play-description__meta"><b>10 tầng</b><i /> <b>3 tim</b><i /> <b>1 đỉnh cao</b></span>
              </span>
            </div>
          </article>

          {/* Game 2: Vẽ & đoán */}
          <article className="play-item">
            <button
              id="game-draw"
              className="play-banner draw-banner scroll-mt-24"
              type="button"
              onClick={() => onOpenGame('draw')}
              aria-label="Chơi Vẽ và đoán"
              aria-describedby="game-draw-description"
            >
              <img
                src="/images/generated/game-draw-guess-realistic-v1.webp"
                alt="Trải nghiệm vẽ và đoán tại bảo tàng"
              />
              <span className="banner-overlay" />
              <span className="banner-copy">
                <small>🎨 02 / SÁNG TẠO HỘI HỌA</small>
                <strong>Vẽ & đoán tác phẩm</strong>
                <span>
                  VẼ NÉT TÀI HOA <i aria-hidden="true">↗</i>
                </span>
              </span>
              <span className="play-arrow" aria-hidden="true">
                ↗
              </span>
            </button>
            <div className="play-description" id="game-draw-description">
              <span className="play-description__body">
                <span className="play-description__eyebrow">XƯỞNG VẼ BẤT NGỜ</span>
                <strong>Vẽ một nét, kể một câu chuyện.</strong>
                <span className="play-description__copy">Bạn tạo manh mối bằng nét vẽ, người chơi khác đoán tác phẩm. Không cần vẽ đẹp — chỉ cần đủ gợi!</span>
                <span className="play-description__meta"><b>3 lượt vẽ</b><i /> <b>15 lượt đoán</b><i /> <b>điểm chung</b></span>
              </span>
            </div>
          </article>

          {/* Game 3: Ghép tranh */}
          <article className="play-item">
            <button
              id="game-puzzle"
              className="play-banner puzzle-banner scroll-mt-24"
              type="button"
              onClick={() => onOpenGame('puzzle')}
              aria-label="Chơi Ghép tranh"
              aria-describedby="game-puzzle-description"
            >
              <img
                src="/images/generated/game-jigsaw-realistic-v1.webp"
                alt="Người chơi ghép những mảnh tranh"
              />
              <span className="banner-overlay" />
              <span className="banner-copy">
                <small>🧩 03 / KHÉO LÉO & NHANH TAY</small>
                <strong>Ghép tranh di sản</strong>
                <span>
                  RÁP MẢNH BẢO VẬT <i aria-hidden="true">↗</i>
                </span>
              </span>
              <span className="play-arrow" aria-hidden="true">
                ↗
              </span>
            </button>
            <div className="play-description" id="game-puzzle-description">
              <span className="play-description__body">
                <span className="play-description__eyebrow">PHÒNG PHỤC CHẾ KIỆT TÁC</span>
                <strong>Kiệt tác đang chờ được ráp lại.</strong>
                <span className="play-description__copy">Chạm, kéo và đổi chỗ các mảnh ảnh để khôi phục tác phẩm trước khi đồng hồ chạy hết. Chọn mức độ hợp tay bạn.</span>
                <span className="play-description__meta"><b>2×2</b><i /> <b>3×3</b><i /> <b>4×4</b><i /> <b>đua thời gian</b></span>
              </span>
            </div>
          </article>
        </aside>
      </section>

      {/* World Footer */}
      <footer className="world-footer">
        <span>BẢO TÀNG MỸ THUẬT VIỆT NAM</span>
        <span className="footer-rule" />
        <span>Version 2.0 · Trải nghiệm mẫu</span>
      </footer>
    </main>
  );
};
